import React from 'react';
import { Helmet } from 'react-helmet';
import {
  useLoungeById,
  useLoungeHoursInLocaleFormat,
  useLoungeAmenitiesWithLocaleTranslations,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn, formatText } from '@/helpers/utils';
import LoungeDetailsOverview from './LoungeDetailsOverview';
import LoungeLocationDetails from './LoungeLocationDetails';
import LoungeDetailsOpeningHours from './LoungeDetailsOpeningHours';
import LoungeDetailsAmenitiesList from './LoungeDetailsAmenitiesList';
import LoungeDetailsSidebar from './LoungeDetailsSidebar';
import LoungeImportantInformation from './LoungeImportantInformation';
import LoungeDetailsProviders from './LoungeDetailsProviders';
import styles from './LoungeDetails.module.scss';
import LoungeAirportandTerminal from '../common/LoungeAirportandTerminal';
import LoungeOtherLoungesInTerminal from '../common/LoungeOtherLoungesInTerminal';

const LoungeDetailsContent = () => {
  const { loungeId } = useRouteParams();
  const lounge = useLoungeById(loungeId);
  const localeLoungeHours = useLoungeHoursInLocaleFormat(loungeId);
  const amenities = useLoungeAmenitiesWithLocaleTranslations(loungeId);

  // for meta description
  const content = useCommonContent();
  const loungeName = lounge.name;
  const airportName = lounge.airport.name;
  const airportIata = lounge.airport.name;
  const airportLat = lounge.airport.location.latitude;
  const airportLong = lounge.airport.location.longitude;
  const loungeTerminal = lounge.location.terminal;
  const loungeDirections = lounge.location.terminal_directions;
  const loungePage = content?.pageTitles?.lounge;
  const googleSearchSchemaAmenities = amenities.map((amenity) => ({
    name: amenity.label,
  }));

  const googleSearchJsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'LocalBusiness',
    name: loungeName,
    amenityFeature: googleSearchSchemaAmenities,
    serviceLocation: `${loungeTerminal}, ${loungeDirections}`,
    containedInPlace: {
      '@type': 'Airport',
      iataCode: airportIata,
      name: airportName,
      latitude: airportLat,
      longitude: airportLong,
    },
  };

  if (Array.isArray(localeLoungeHours)) {
    const googleSearchSchemaFormattedHours = localeLoungeHours.map(
      ({ dayTranslated, firstOpenTime, firstCloseTime }) =>
        `${dayTranslated} ${firstOpenTime}-${firstCloseTime}` // "Monday 9:00-17:00", "Tuesday 06:30-20:30"
    );
    googleSearchJsonLd.openingHours = googleSearchSchemaFormattedHours;
  }

  return (
    <div
      className={cn('pad-0-t pad-2-r-min-up width-full container tw-max-w-360')}
    >
      <Helmet>
        <title>
          {formatText(loungePage?.title, { loungeName, airportName })}
        </title>
        <meta
          name="description"
          content={formatText(loungePage?.description, {
            loungeName,
            airportName,
            loungeTerminal,
          })}
        />
        {/* https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data */}
        <script type="application/ld+json">
          {JSON.stringify(googleSearchJsonLd, null, 2)}
        </script>
      </Helmet>
      <div className="row stack position-relative">
        <div
          id={styles.loungeContentContainer}
          className="col-xs-12 col-sm-12 col-md-6 col-lg-7 col-xl-8 position-static-md-up margin-0-b"
        >
          <h1 className="tw-mr-2 tw-pb-1 tw-font-serif tw-text-dls36 tw-font-normal tw-text-gray-900 md_tw-text-48px">
            {loungeName}
          </h1>
          <LoungeAirportandTerminal />
          <LoungeDetailsOverview />
          <LoungeDetailsSidebar className="col-xs-12 col-md-5 col-lg-4 col-xl-4 md_tw-hidden" />
          <LoungeDetailsOpeningHours />
          <LoungeLocationDetails />
          <LoungeImportantInformation />
          <LoungeDetailsAmenitiesList />
        </div>
        <LoungeDetailsSidebar className="col-xs-12 col-md-6 col-lg-5 col-xl-4 tw-hidden md_tw-block" />
        <LoungeDetailsProviders />
        <LoungeOtherLoungesInTerminal />
      </div>
    </div>
  );
};

export default LoungeDetailsContent;
